import React from 'react';
import { IdashboardItem, IdashboardData } from '../../models';
import PartsIcon from '../common/PartsIcon';
import { format } from 'date-fns';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { TFunction } from 'react-i18next';
import downloadIcon from '../../download.svg';
import { DashboardColumn } from '../../types/dashboard';

export const getMobileColumns = ({
    t,
    data,
    getJobIsDownloaded
}: {
    t: TFunction;
    data: IdashboardData;
    getJobIsDownloaded?: (jobID: string) => boolean;
}): DashboardColumn[] => {
    return [
        {
            Header: ({ state, toggleAllRowsSelected }: any) => {
                const { selectedRowIds } = state;
                const checkedCount = selectedRowIds ? Object.keys(selectedRowIds).length : 0;
                const atLeastOneItemChecked = checkedCount > 0;

                return (
                    <div className="d-flex justify-content-center">
                        <input
                            type="checkbox"
                            onClick={e => e.stopPropagation()}
                            onChange={e => toggleAllRowsSelected(e.target.checked)}
                            disabled={!atLeastOneItemChecked}
                        />
                    </div>
                );
            },
            id: 'selection',
            accessor: '_selector',
            width: 40,
            minWidth: 40,
            maxWidth: 40,
            Cell: ({ row, state, toggleRowSelected }: any) => {
                const { selectedRowIds } = state;
                const rowData = row.original;
                const jobID = rowData?.job?.id || '';

                // Show download icon if row has a job and it's downloaded
                if (jobID && getJobIsDownloaded && getJobIsDownloaded(jobID)) {
                    return (
                        <div className="d-flex justify-content-center">
                            <img src={downloadIcon} alt="Downloaded" />
                        </div>
                    );
                }

                // Show checkbox if row has no job
                if (!jobID) {
                    return (
                        <div className="d-flex justify-content-center">
                            <input
                                type="checkbox"
                                onClick={e => {
                                    e.stopPropagation();
                                }}
                                checked={selectedRowIds[rowData.id] || false}
                                onChange={e => {
                                    toggleRowSelected(rowData.id, e.target.checked);
                                }}
                            />
                        </div>
                    );
                }

                // Show empty cell if job exists but not downloaded
                return null;
            }
        },
        {
            Header: t('jobNumberTableHeader'),
            id: 'jobNumber',
            accessor: ({ job }: IdashboardItem) => {
                return (
                    <div>
                        <span className="mobile-label">{String(t('jobNumberTableHeader'))}:</span>
                        <span className="mobile-job-number">{job?.jobNumber}</span>
                    </div>
                );
            },
            width: 160,
            minWidth: 80
        },

        {
            Header: t('dashboardAnalytics:Hours'),
            id: 'hours',
            className: 'mobile-hours',
            accessor: ({ workOrder }: IdashboardItem) => (
                <div>
                    <span className="mobile-label">{String(t('dashboardAnalytics:hours'))}</span>
                    <span className="mobile-job-hours">{workOrder?.plannedHours || 0} hrs</span>
                </div>
            ),
            minWidth: 120
        },
        {
            Header: 'Parts',
            id: 'parts',
            className: 'mobile-parts',
            accessor: ({ workOrder }: IdashboardItem) => (
                <div>
                    <span className="mobile-label">{String(t('labelParts'))}</span>
                    <PartsIcon partStatus={workOrder?.partStatus} parts={workOrder?.parts} />
                </div>
            ),
            minWidth: 80
        },
        {
            Header: 'SAP Work Order',
            id: 'workOrderNumber',
            className: 'mobile-work-order-number',
            accessor: ({ workOrder }: IdashboardItem) => (
                <div>
                    <span className="mobile-label">{String(t('manageInventory:sapWoNum'))}</span>
                    <span>{workOrder?.number}</span>
                </div>
            ),
            minWidth: 135
        },
        {
            Header: 'Status',
            id: 'jobStatus',
            className: 'mobile-status',
            accessor: ({ job }: IdashboardItem) => {
                const status = job?.status;
                const displayStatus =
                    status === 'InProgress'
                        ? 'WIP'
                        : status === 'ReviewPending'
                        ? String(t('nsJob:reviewPending'))
                        : status || 'New';
                return (
                    <div>
                        <span className="mobile-label">{String(t('manageWorkOrder:status'))}</span>
                        <span>{displayStatus}</span>
                    </div>
                );
            },
            minWidth: 200
        },
        {
            Header: 'Start Date',
            id: 'startDate',
            className: 'mobile-start-date',
            accessor: ({ startDate }: IdashboardItem) => (
                <div>
                    <span className="mobile-label">{String(t('nsJob:startDate'))}</span>
                    <span>{startDate ? format(new Date(startDate), 'dd-MMM-yyyy') : null}</span>
                </div>
            ),
            minWidth: 135
        },
        {
            Header: 'Order Type',
            id: 'orderType',
            className: 'mobile-order-type',
            accessor: ({ orderType }: IdashboardItem) => (
                <div>
                    <span className="mobile-label">Type</span>
                    <span>{orderType}</span>
                </div>
            ),
            minWidth: 160
        },
        {
            Header: 'Facility',
            id: 'facilityName',
            className: 'mobile-facility-name',
            accessor: ({ facility }: IdashboardItem) => (
                <>
                    <span className="mobile-label">{String(t('nsJob:facility'))}</span>
                    <span className="mobile-description-text">{facility?.name}</span>
                </>
            ),
            minWidth: 380
        },
        {
            Header: 'City',
            id: 'city',
            className: 'mobile-city-name',
            accessor: ({ facility }: IdashboardItem) => (
                <div>
                    <span className="mobile-label">{String(t('user:city'))}</span>
                    <span>{facility?.city}</span>
                </div>
            ),
            minWidth: 330
        },
        {
            Header: 'Serial Number',
            id: 'serialNumber',
            className: 'mobile-serial-number',
            accessor: ({ installBase }: IdashboardItem) => (
                <div>
                    <span className="mobile-label">{String(t('manageInventory:serialNumber'))}</span>
                    <span>{installBase?.serialNumber}</span>
                </div>
            ),
            minWidth: 330
        },
        {
            Header: 'Description',
            id: 'description',
            className: 'mobile-description',
            accessor: ({ workOrder }: IdashboardItem) => (
                <div>
                    <span className="mobile-label">{String(t('manageWorkOrder:description'))}</span>
                    <span>{workOrder?.activityDescription}</span>
                </div>
            ),
            minWidth: 330
        }
    ];
};

export const getColumns = ({
    t,
    data,
    getJobIsDownloaded
}: {
    t: TFunction;
    data: IdashboardData;
    getJobIsDownloaded?: (jobID: string) => boolean;
}): DashboardColumn[] => [
    {
        Header: ({ state, toggleAllRowsSelected }: any) => {
            const { selectedRowIds } = state;
            const checkedCount = selectedRowIds ? Object.keys(selectedRowIds).length : 0;
            const atLeastOneItemChecked = checkedCount > 0;

            return (
                <div className="d-flex justify-content-center">
                    <input
                        type="checkbox"
                        onClick={e => e.stopPropagation()}
                        onChange={e => toggleAllRowsSelected(e.target.checked)}
                        disabled={!atLeastOneItemChecked}
                    />
                </div>
            );
        },
        id: 'selection',
        accessor: '_selector',
        width: 40,
        minWidth: 40,
        maxWidth: 40,
        Cell: ({ row, state, toggleRowSelected }: any) => {
            const { selectedRowIds } = state;
            const rowData = row.original;
            const jobID = rowData?.job?.id || '';

            // Show download icon if row has a job and it's downloaded
            if (jobID && getJobIsDownloaded && getJobIsDownloaded(jobID)) {
                return (
                    <div className="d-flex justify-content-center">
                        <img src={downloadIcon} alt="Downloaded" />
                    </div>
                );
            }

            // Show checkbox if row has no job
            if (!jobID) {
                return (
                    <div className="d-flex justify-content-center">
                        <input
                            type="checkbox"
                            onClick={e => {
                                e.stopPropagation();
                            }}
                            checked={selectedRowIds[rowData.id] || false}
                            onChange={e => {
                                toggleRowSelected(rowData.id, e.target.checked);
                            }}
                        />
                    </div>
                );
            }

            // Show empty cell if job exists but not downloaded
            return null;
        }
    },
    {
        Header: String(t('manageWorkOrder:status')),
        id: 'jobStatus',
        accessor: ({ job }: IdashboardItem) => {
            const status = job?.status;
            const displayStatus =
                status === 'InProgress'
                    ? 'WIP'
                    : status === 'ReviewPending'
                    ? String(t('nsJob:reviewPending'))
                    : status || 'New';
            return <span>{displayStatus}</span>;
        },
        width: 90,
        minWidth: 80,
        maxWidth: 100
    },
    {
        Header: String(t('jobNumberTableHeader')),
        id: 'jobNumber',
        accessor: ({ job }: IdashboardItem) => (
            <OverlayTrigger
                placement="top"
                trigger={['hover', 'focus']}
                delay={250}
                overlay={
                    <Tooltip id="job-number-tooltip" className="custom-tooltip">
                        {job?.jobNumber}
                    </Tooltip>
                }
            >
                <span>{job?.jobNumber}</span>
            </OverlayTrigger>
        ),
        width: 127,
        minWidth: 124,
        maxWidth: 130
    },
    {
        Header: String(t('manageInventory:sapWoNum')),
        id: 'workOrderNumber',
        className: 'work-order-number',
        accessor: ({ workOrder }: IdashboardItem) => <span>{workOrder?.number}</span>,
        width: 105,
        minWidth: 90,
        maxWidth: 120
    },
    {
        Header: String(t('nsJob:facility')),
        id: 'facilityName',
        accessor: ({ facility }: IdashboardItem) => {
            const facilityName = facility?.name;
            if (facilityName?.length > 14) {
                return (
                    <OverlayTrigger
                        placement="top"
                        trigger={['hover', 'focus']}
                        delay={250}
                        overlay={
                            <Tooltip id="facility-tooltip" className="custom-tooltip">
                                {facility?.name}
                            </Tooltip>
                        }
                    >
                        <span>{facility?.name}</span>
                    </OverlayTrigger>
                );
            }

            return <span>{facility?.name}</span>;
        },
        width: 175,
        minWidth: 150,
        maxWidth: 200
    },
    {
        Header: String(t('description')),
        id: 'description',
        accessor: ({ workOrder }: IdashboardItem) => (
            <OverlayTrigger
                placement="top"
                trigger={['hover', 'focus']}
                delay={250}
                overlay={
                    <Tooltip id="description-tooltip" className="custom-tooltip">
                        {workOrder?.activityDescription}
                    </Tooltip>
                }
            >
                <span>{workOrder?.activityDescription}</span>
            </OverlayTrigger>
        ),
        width: 145,
        minWidth: 145
    },
    {
        Header: String(t('user:city')),
        id: 'city',
        accessor: ({ facility }: IdashboardItem) => <span>{facility?.city}</span>,
        width: 125,
        minWidth: 100,
        maxWidth: 150
    },
    {
        Header: String(t('nsJob:startDate')),
        id: 'startDate',
        accessor: ({ startDate }: IdashboardItem) => (
            <span>{startDate ? format(new Date(startDate), 'dd-MMM-yy') : null}</span>
        ),
        width: 112,
        minWidth: 100,
        maxWidth: 125
    },
    {
        Header: String(t('nsJob:orderType')),
        id: 'orderType',
        accessor: ({ orderType }: IdashboardItem) => <span>{orderType}</span>,
        width: 122,
        minWidth: 115,
        maxWidth: 130
    },
    {
        Header: () => {
            return (
                <div>
                    <span>{String(t('dashboardAnalytics:Hours'))}</span>
                    <span className="hl">{data.totalHours}</span>
                </div>
            );
        },
        id: 'hours',
        accessor: ({ workOrder }: IdashboardItem) => <span>{workOrder?.plannedHours || 0} hrs</span>,
        width: 65,
        minWidth: 65,
        maxWidth: 65
    },
    {
        Header: String(t('manageInventory:serialNumber')),
        id: 'serialNumber',
        accessor: ({ installBase }: IdashboardItem) => {
            if (installBase?.serialNumber?.length && installBase?.serialNumber?.length < 7) {
                return <span>{installBase?.serialNumber}</span>;
            }

            return (
                <OverlayTrigger
                    placement="top"
                    trigger={['hover', 'focus']}
                    delay={250}
                    overlay={
                        <Tooltip id="serial-number-tooltip" className="custom-tooltip">
                            {installBase?.serialNumber}
                        </Tooltip>
                    }
                >
                    <span>{installBase?.serialNumber}</span>
                </OverlayTrigger>
            );
        },
        width: 100,
        minWidth: 85,
        maxWidth: 115
    },
    {
        Header: String(t('labelParts')),
        id: 'partStatus',
        accessor: ({ workOrder }: IdashboardItem) => (
            <PartsIcon partStatus={workOrder?.partStatus} parts={workOrder?.parts} />
        ),
        width: 90,
        minWidth: 80,
        maxWidth: 100
    }
];
